import { Code, CodeFilters, DecryptedContact, DecryptedForm, DecryptedPatient, PaginatedListIterator, randomUuid } from '@icure/cardinal-sdk'
import { createSelector } from '@reduxjs/toolkit'
import React, { useCallback, useEffect, useState } from 'react'
import { v4 as uuid } from 'uuid'
import { useCreateContactMutation, useFindContactsWithFormIdsQuery, useModifyContactMutation } from '../../../../core/api/contactApi'
import { useGetPractitionerQuery } from '../../../../core/api/practitionerApi'
import { useAppSelector } from '../../../../core/hooks'
import { CardinalApiState, getApiFromState } from '../../../../core/services/auth.api'
import { getNumericDate } from '../../../../helpers/date'

import { CustomModal } from '../../../common/CustomModal'
import './index.less'
import { DecoratedForm } from '../../../Form/lit-decorated-form'
import { Form } from '@icure/form/components/model'

import { useGetUserQuery } from '../../../../core/api/userApi'
import { Suggestion } from '@icure/form/generic'
import { SpinLoader } from '../../../common/SpinLoader'
import { useCreateFormMutation, useDeleteFormMutation, useGetAllChildrenOfFormWithIdQuery, useGetFormWithIdQuery, useModifyFormMutation } from '../../../../core/api/formApi'
import { useGetFormTemplatesByGuidQuery, useGetFormTemplateAttachmentQuery } from '../../../../core/api/formTemplateApi'
import { deepEquals } from '../../../../helpers/equal'
import { Button, message } from 'antd'
import { useLazyGeneratePdfReportQuery } from '../../../../core/api/pdfReportApi'
import { MedicalReportPDF } from '../../components/MedicalReportPDF'
import { usePDF } from '@react-pdf/renderer'
import { translateSection } from '../../../../helpers/translate'

interface modalAddConsultationFormFormProps {
  isVisible: boolean
  onClose: () => void
  patient: DecryptedPatient
  initialRootFormId?: string
}

const reduxSelector = createSelector(
  (state: { cardinalApi: CardinalApiState }) => state.cardinalApi,
  (cardinalApi: CardinalApiState) => ({
    userId: cardinalApi.user?.id,
    healthcarePartyId: cardinalApi.user?.healthcarePartyId,
    parentOrOwnHealthcarePartyId: cardinalApi.healthcareParty?.parentId ?? cardinalApi.user?.healthcarePartyId,
    sdkLoader: getApiFromState(() => cardinalApi),
  }),
)

export const ModalAddConsultationForm = ({ isVisible, onClose, patient, initialRootFormId }: modalAddConsultationFormFormProps) => {
  const [formTemplate, setFormTemplate] = React.useState<Form | undefined>()
  const { data: allTemplateVersions } = useGetFormTemplatesByGuidQuery()
  const latestTemplateId = allTemplateVersions?.[0]?.id

  const startOfTheConsultation = getNumericDate(new Date())
  const { userId, healthcarePartyId, parentOrOwnHealthcarePartyId, sdkLoader } = useAppSelector(reduxSelector)
  const { data: practitioner } = useGetPractitionerQuery(healthcarePartyId ?? '', { skip: !healthcarePartyId })
  const { data: user } = useGetUserQuery(userId ?? '', { skip: !userId })
  const [childrenForms, setChildrenForms] = React.useState<Record<string, DecryptedForm> | undefined>(undefined)
  const [initialForms, setInitialForms] = React.useState<Record<string, DecryptedForm> | undefined>(undefined)
  const { data: initialRootDecryptedForm } = useGetFormWithIdQuery({ id: initialRootFormId ?? '' }, { skip: !initialRootFormId })

  // For existing forms, use the stored formTemplateId; for new forms, use latest
  const resolvedTemplateId = initialRootDecryptedForm?.formTemplateId && initialRootDecryptedForm.formTemplateId !== 'Preventi.yaml'
    ? initialRootDecryptedForm.formTemplateId
    : latestTemplateId

  const { data: resolvedForm } = useGetFormTemplateAttachmentQuery(resolvedTemplateId ?? '', {
    skip: !resolvedTemplateId,
  })

  // Fall back to latest if specific version not found
  const { data: fallbackForm } = useGetFormTemplateAttachmentQuery(latestTemplateId ?? '', {
    skip: !latestTemplateId || !!resolvedForm || resolvedTemplateId === latestTemplateId,
  })

  useEffect(() => {
    const loaded = resolvedForm ?? fallbackForm
    if (loaded) {
      setFormTemplate(loaded)
    }
  }, [resolvedForm, fallbackForm])
  const { data: initialAllChildrenForms } = useGetAllChildrenOfFormWithIdQuery(
    { hcPartyId: parentOrOwnHealthcarePartyId ?? '', id: initialRootFormId ?? '' },
    { skip: !initialRootFormId || !parentOrOwnHealthcarePartyId },
  )
  const { data: contactsHistory } = useFindContactsWithFormIdsQuery(
    { hcPartyId: parentOrOwnHealthcarePartyId ?? '', formIds: Object.values(initialForms ?? {}).map((f) => f.id) ?? [] },
    { skip: !Object.keys(initialForms ?? {}).length || !parentOrOwnHealthcarePartyId },
  )
  const [updatedContact, setUpdatedContact] = useState<DecryptedContact | undefined>(undefined)
  const [generateReport, { currentData: pdfData, requestId }] = useLazyGeneratePdfReportQuery()

  const [pdfInstance, updatePdfInstance] = usePDF()

  const [saveAndClose, setSaveAndClose] = useState(false)
  const [saveAndGenerateReport, setSaveAndGenerateReport] = useState(false)

  useEffect(() => {
    if (pdfInstance.url) {
      window.open(pdfInstance.url, '_blank')
    }
  }, [pdfInstance.url])

  const patientData = pdfData?.patientData
  const medicalOverview = pdfData?.medicalOverview
  const initialEvaluationData = pdfData?.initialEvaluationData
  const wellBeingSummary = pdfData?.wellBeingSummary

  useEffect(() => {
    if (patientData && medicalOverview && initialEvaluationData && wellBeingSummary) {
      translateSection(patient?.languages?.[0] ?? 'fr', 'medicalReportPDF').then((translations) => {
        const medicalReportPDF = (
          <MedicalReportPDF
            translations={translations}
            patientData={patientData}
            medicalOverview={medicalOverview}
            initialEvaluationData={initialEvaluationData}
            wellBeingSummary={wellBeingSummary}
          />
        )

        updatePdfInstance(medicalReportPDF)
      })
    }
  }, [patientData, medicalOverview, initialEvaluationData, wellBeingSummary, requestId])

  const [createContact] = useCreateContactMutation()
  const [modifyContact] = useModifyContactMutation()
  const [createForm] = useCreateFormMutation()
  const [modifyForm] = useModifyFormMutation()
  const [deleteForm] = useDeleteFormMutation()

  const language = patient.languages[0] ?? 'fr'

  const [contact, setContact] = useState(
    new DecryptedContact({
      id: uuid(),
      descr: `${practitioner?.speciality ?? 'Preventi'} consultation`,
      openingDate: startOfTheConsultation,
      services: [],
      subContacts: [],
    }),
  )

  const [rootForm, setRootForm] = React.useState<DecryptedForm | undefined>(undefined)

  useEffect(() => {
    if (!initialRootFormId && latestTemplateId && !rootForm) {
      setRootForm(
        new DecryptedForm({
          id: randomUuid(),
          openingDate: startOfTheConsultation,
          descr: `Preventi ${new Date().toISOString()}`,
          formTemplateId: latestTemplateId,
        }),
      )
      setChildrenForms({})
    }
  }, [latestTemplateId, initialRootFormId])

  useEffect(() => {
    if (initialRootDecryptedForm && initialAllChildrenForms) {
      setRootForm(initialRootDecryptedForm)
      const allChildrenForms = initialAllChildrenForms.reduce((acc, form) => ({ ...acc, [form.id]: form }), {})
      setInitialForms({ [initialRootDecryptedForm.id]: initialRootDecryptedForm, ...allChildrenForms })
      setChildrenForms(allChildrenForms)
    }
  }, [initialRootDecryptedForm, initialAllChildrenForms])

  const ownersProvider = useCallback(
    async (terms: string[], ids?: string[], specialties?: string[]) => {
      const sdk = await sdkLoader

      if (!sdk) {
        return []
      }

      const longestTerm = terms.reduce((w, t) => (w.length >= t.length ? w : t), '')
      const candidates = await sdk.healthcareParty.findHealthcarePartiesByName({ name: longestTerm })
      return (candidates.rows || [])
        .filter((hcp) =>
          terms.every(
            (t) =>
              (hcp.name?.toLowerCase().includes(t.toLowerCase()) ||
                hcp.firstName?.toLowerCase().includes(t.toLowerCase()) ||
                hcp.lastName?.toLowerCase().includes(t.toLowerCase())) &&
              (!ids?.length || ids.includes(hcp.id)) &&
              (!specialties?.length || specialties.some((s) => hcp.speciality?.includes(s))),
          ),
        )
        .map((x) => ({ id: x.id, text: x.name, terms: terms, label: {} }) as Suggestion)
    },
    [sdkLoader],
  )

  const saveForm = async () => {
    try {
      const result = (await (updatedContact?.rev ? modifyContact(updatedContact) : createContact({ patient, contact: updatedContact ?? contact }))) as
        | { data: DecryptedContact }
        | undefined
      if (result?.data) {
        setUpdatedContact(result.data)
        setContact(result.data)
      } else {
        message.warning('The consultation form could not be saved. Please retry in a few minutes or contact support.')
      }
    } catch (e) {
      message.warning('The contact could not be saved. Please retry in a few minutes or contact support.')
    }

    if (rootForm && childrenForms) {
      if (initialForms) {
        ;[...Object.values(childrenForms), rootForm].forEach((form) => {
          if (initialForms[form.id] && !deepEquals(initialForms[form.id], form)) {
            if (form.rev) {
              modifyForm(form)
            } else {
              createForm({ patient, form })
            }
          } else if (!initialForms?.[form.id]) {
            createForm({ patient, form })
          }
        })
        ;[...Object.values(initialForms)].forEach((form) => {
          if (form.id !== rootForm.id && !childrenForms[form.id]) {
            deleteForm(form)
          }
        })
      } else {
        ;[...Object.values(childrenForms), rootForm].forEach((form) => {
          if (form.rev) {
            modifyForm(form)
          } else {
            createForm({ patient, form })
          }
        })
      }
    }
  }

  useEffect(() => {
    if (saveAndClose) {
      setSaveAndClose(false)
      saveForm().then(() => {
        onClose()
      })
    }
  }, [saveAndClose, updatedContact, rootForm, childrenForms, initialForms, contact])

  useEffect(() => {
    if (saveAndGenerateReport) {
      saveForm().then(() =>
        generateReport({
          language: patient?.languages?.[0],
          patient,
          formId: initialRootFormId,
        }),
      )
      setSaveAndGenerateReport(false)
    }
  }, [saveAndGenerateReport, updatedContact, rootForm, childrenForms, initialForms, contact])

  const handleOnClose = () => {
    onClose()
  }

  const optionsProvider = async (language: string, codifications: string[], searchTerms: string[]) => {
    const codeSdk = (await sdkLoader)?.code
    return codeSdk
      ? Promise.all(
          codifications.map((c) => {
            const type = c.split('-')[0]
            const longestTerm = searchTerms?.reduce((w, t) => (w.length >= t.length ? w : t), '')

            return codeSdk.filterCodesBy(CodeFilters.byLanguageTypeLabelRegion(language, type, { label: longestTerm })).then(async (codes: PaginatedListIterator<Code>) => {
              return { type, codes: await codes.next(100) }
            })
          }),
        )
          .then((results) => results.flatMap((r) => r.codes))
          .then((codes) => codes.map((c) => ({ ...c, label: c.label ?? {} })))
      : Promise.resolve([])
  }

  const onContactModified = async (contact: DecryptedContact) => {
    setUpdatedContact(contact)
    return contact
  }
  const onFormAddedOrModified = async (form: DecryptedForm) => {
    setChildrenForms({ ...childrenForms, [form.id]: form })
    return form
  }
  const onFormDeleted = async (formId: string) => {
    setChildrenForms(Object.entries(childrenForms ?? {}).reduce((acc, [k, f]) => (k !== formId ? { ...acc, [k]: f } : acc), {}))
  }

  console.log('rootForm', rootForm)
  console.log('childrenForms', childrenForms)
  console.log('contactsHistory', contactsHistory)

  const customFooter = [
    <div className="modalAddConsultationForm__footer" key="footer">
      <Button
        key="report"
        onClick={() => {
          if (document.activeElement && document.activeElement instanceof HTMLElement) {
            ;(document.activeElement as HTMLElement).blur?.()
          }
          setTimeout(() => setSaveAndGenerateReport(true), 0)
        }}
        disabled={!initialRootFormId || !patient}
      >
        Generate report
      </Button>
      <div className="modalAddConsultationForm__footer__mainButtons">
        <Button key="back" onClick={handleOnClose}>
          Cancel
        </Button>
        <Button
          key="submit"
          type="primary"
          onClick={() => {
            if (document.activeElement && document.activeElement instanceof HTMLElement) {
              ;(document.activeElement as HTMLElement).blur?.()
            }
            setTimeout(() => setSaveAndClose(true), 0) // Timeout to ensure that the state has been set before the save occurs
          }}
        >
          Save
        </Button>
      </div>
    </div>,
  ]

  return (
    <CustomModal width={1200} isVisible={isVisible} handleClose={handleOnClose} title="Add consultation" blockAntModalBodyVerticalScroll customFooter={customFooter}>
      <div className="modalAddConsultationForm">
        {formTemplate && rootForm && childrenForms && (contactsHistory || !initialRootFormId) ? (
          <DecoratedForm
            form={formTemplate}
            language={language}
            patient={patient}
            rootCardinalForm={rootForm}
            currentContact={contact}
            contactsHistory={contactsHistory ?? []}
            sdkLoader={sdkLoader}
            author={user}
            responsible={practitioner}
            ownersProvider={ownersProvider}
            optionsProvider={optionsProvider}
            contactModified={onContactModified}
            formAddedOrModified={onFormAddedOrModified}
            formDeleted={onFormDeleted}
          />
        ) : (
          <SpinLoader />
        )}
      </div>
    </CustomModal>
  )
}
